// AllExports.js - WITH ROTATING MESSAGES
import React from 'react';
import './AllExports.css';

function AllExports({
  // PNG Export props
  showPNGModal,
  setShowPNGModal,
  processedSlides,
  selectedSlides,
  handleSelectAllSlides,
  handleToggleSlide,
  handleExportPNG,
  isExportingPNG,
  
  // MP4 Export props
  showMP4Modal,
  setShowMP4Modal,
  isGeneratingVideo,
  planTracker,
  mp4Transition,
  setMp4Transition,
  mp4Duration,
  setMp4Duration,
  audioUrl,
  uploadMode,
  includeAudio,
  setIncludeAudio,
  audioExpiresAt,
  videoGenerationProgress,
  videoGenerationError,
  handleExportMP4Local,
}) {
  // ✅ Helper: Check if slides have timing data
  const hasTiming = processedSlides.some(slide => slide.duration && slide.duration > 0);

  // ✅ Rotating messages state
  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);
  
  const loadingMessages = [
    "This might take a few minutes, grab a coffee ☕",
    "Quality takes time, you'll love this 🌟",
    "Your HD masterpiece is getting created 🎬",
    "Creation takes time, wait here warrior ⚔️",
     "Your patience will be rewarded 🎁",
     "Don't close this tab, we're almost there 🚀",
    "Cooking up something special 🍳",
    "Audio sync magic is happening ✨",
    "Stay with us, magic is happening right now ✨",
  "Processing high-quality video frame by frame 🎞️",
    "Putting the final touches ✨",
    "Finalizing your masterpiece 🎨"
  ];

  // ✅ Rotate messages every 35 seconds
  React.useEffect(() => {
    if (isGeneratingVideo) {
      const interval = setInterval(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 35000); // 35 seconds

      return () => clearInterval(interval);
    } else {
      setCurrentMessageIndex(0); // Reset when not generating
    }
  }, [isGeneratingVideo, loadingMessages.length]);

  return (
    <>
      {/* PNG Export Modal */}
      {showPNGModal && (
        <div className="export-modal-overlay" onClick={() => setShowPNGModal(false)}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <h3>Download as PNG</h3>
              <button className="modal-close-btn" onClick={() => setShowPNGModal(false)}>×</button>
            </div>
            
            <div className="export-modal-body">
              <div className="select-all-row">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedSlides.size === processedSlides.length}
                    onChange={handleSelectAllSlides}
                  />
                  Select all ({processedSlides.length} slides)
                </label>
              </div>

              <div className="slides-selection-list">
                {processedSlides.map((slide, index) => (
                  <label key={index} className="slide-selection-item">
                    <input
                      type="checkbox"
                      checked={selectedSlides.has(index)}
                      onChange={() => handleToggleSlide(index)}
                    />
                    <span className="slide-label">Slide {index + 1}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="export-modal-footer">
              <button 
                className="export-cancel-btn"
                onClick={() => setShowPNGModal(false)}
              >
                Cancel
              </button>
              <button 
                className="export-download-btn"
                onClick={handleExportPNG}
                disabled={selectedSlides.size === 0 || isExportingPNG}
              >
                {isExportingPNG ? 'Downloading...' : `Download ${selectedSlides.size} slide${selectedSlides.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MP4 Export Modal */}
      {showMP4Modal && (
        <div className="export-modal-overlay" onClick={() => setShowMP4Modal(false)}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <h3>Export as HD Video</h3>
              <button className="modal-close-btn" onClick={() => setShowMP4Modal(false)}>×</button>
            </div>

            <div className="export-modal-body">
              {!isGeneratingVideo ? (
                <>
                  {/* ✅ Audio sync checkbox - shows when slides have timing data */}
                  {hasTiming && (
                    <>
                      <div className="export-option">
                        <label className="audio-checkbox-label">
                          <input
                            type="checkbox"
                            checked={includeAudio}
                            onChange={(e) => setIncludeAudio(e.target.checked)}
                          />
                          <span style={{ fontSize: '16px', marginLeft: '10px' }}>
                            Include original audio (synced with slides)
                          </span>
                        </label>
                        {includeAudio && (
                          <div className="audio-sync-notice">
                            ✓ Audio will be synchronized with slide timings automatically
                          </div>
                        )}
                      </div>

                      {/* ✅ Audio sync info - shows when audio is enabled */}
                      {includeAudio && (
                        <div className="audio-sync-info">
                          <strong>🎵 Audio Sync Mode</strong>
                          <p>
                            Slide durations will match audio timing automatically. Video length: ~{processedSlides.reduce((sum, s) => sum + (s.duration || 3), 0).toFixed(0)} seconds
                          </p>
                        </div>
                      )}
<p className="export-info-subtext" style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
  e.g ⏱️ A 5-minute video takes ~5 to 7 minutes to generate. Keep this tab open!
</p>
                      {/* ✅ OR divider - always show when timing exists */}
                      <div style={{ textAlign: 'center', margin: '15px 0', color: '#666', fontWeight: '500' }}>
                        OR
                      </div>
                    </>
                  )}

                  {/* ✅ Duration per slide - always show, but disabled when audio sync is on */}
                  <div className="export-option">
                    <label>Duration per slide (seconds)</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={mp4Duration}
                      onChange={(e) => setMp4Duration(Number(e.target.value))}
                      disabled={includeAudio && hasTiming}
                      style={includeAudio && hasTiming ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* ✅ Total video length info - always show */}
                  <div className="export-info" style={includeAudio && hasTiming ? { opacity: 0.5 } : {}}>
                    Total video length: ~{(processedSlides.length * mp4Duration).toFixed(0)} seconds
                    <br />
                    <span className="export-info-subtext">
                      Each slide will show for {mp4Duration} seconds
                    </span>
                  </div>
                </>
              ) : (
                <div className="export-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${videoGenerationProgress}%` }}
                    />
                    
                  </div>
                  <p className="progress-percentage">{videoGenerationProgress}%</p>
                  {videoGenerationError && (
                    <p className="error-text">{videoGenerationError}</p>
                  )}
                  <p className="rotating-message">
                    {loadingMessages[currentMessageIndex]}
                    <span className="animated-dots">
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                    </span>
                  </p>
                  
                </div>
              )}
            </div>

            <div className="export-modal-footer">
              <button 
                className="export-cancel-btn"
                onClick={() => setShowMP4Modal(false)}
                disabled={isGeneratingVideo}
              >
                Cancel
              </button>
              <button 
                className="export-download-btn"
                onClick={handleExportMP4Local}
                disabled={isGeneratingVideo}
              >
                {isGeneratingVideo ? 'Generating...' : 'Generate HD Video'}
              </button>
            </div>
          
          </div>
        </div>
      )}
    </>
  );
}

export default AllExports;